import { CSSProperties, useEffect, useRef } from 'react'
import { BehaviorMapEventHandler, YMap, YMapMarker } from '@yandex/ymaps3-types'
import { YMapClusterer } from '@yandex/ymaps3-types/packages/clusterer'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import { useNavigate } from 'react-router-dom'
import urls from '../../navigation/urls.ts'
import mapConstants from './map.constants.ts'
import mapEquipmentRenderer from '@/shared/Map/map.equipmentRenderer.ts'
import useSkipFirstRenderEffect from '@/shared/hooks/useSkipFirstRenderEffect.ts'
import css from './map.module.css'

export type TMapBounds = {
  rectangleTopLeftPoint: { latitude: number; longitude: number }
  rectangleBottomRightPoint: { latitude: number; longitude: number }
}

interface IProps {
  onResize: (bounds: TMapBounds) => void
  equipment: IEquipment[]
  mapBounds: TMapBounds
  wrapperStyle?: CSSProperties
}

const Map = (props: IProps) => {
  const navigate = useNavigate()

  const ref = useRef<HTMLDivElement>()
  const mapRef = useRef<YMap>(null)
  const popup = useRef<YMapMarker>(null)
  const clustererRef = useRef<YMapClusterer>(null)
  const rendered = useRef(false)

  useEffect(() => {
    if (rendered.current || !ref.current) return

    rendered.current = true
    const renderMap = async () => {
      await ymaps3.ready
      const { YMapClusterer, clusterByGrid } = await ymaps3.import('@yandex/ymaps3-clusterer@0.0.1')

      const map = new ymaps3.YMap(
        ref.current,
        {
          showScaleInCopyrights: true,
          location: props.mapBounds
            ? {
                bounds: [
                  [props.mapBounds.rectangleTopLeftPoint.longitude, props.mapBounds.rectangleTopLeftPoint.latitude],
                  [
                    props.mapBounds.rectangleBottomRightPoint.longitude,
                    props.mapBounds.rectangleBottomRightPoint.latitude,
                  ],
                ],
              }
            : mapConstants.defaultLocation,
          behaviors: ['drag', 'pinchZoom', 'mouseTilt', 'scrollZoom'],
          mode: 'vector',
        },
        [
          new ymaps3.YMapDefaultSchemeLayer({}),
          new ymaps3.YMapDefaultFeaturesLayer({}),
          new ymaps3.YMapFeatureDataSource({ id: 'equipment' }),
          new ymaps3.YMapLayer({ source: 'equipment', type: 'markers', zIndex: 1800 }),
        ]
      )

      /* Handle map viewport change */
      const handleActionEnd: BehaviorMapEventHandler = (data) => {
        // map resize, viewport changed
        if (
          data.type === 'drag' ||
          data.type === 'scrollZoom' ||
          data.type === 'pinchZoom' ||
          data.type === 'oneFingerZoom'
        ) {
          props.onResize({
            rectangleTopLeftPoint: { latitude: data.location.bounds[0][1], longitude: data.location.bounds[0][0] },
            rectangleBottomRightPoint: { latitude: data.location.bounds[1][1], longitude: data.location.bounds[1][0] },
          })
        }
      }

      /* Clusters and markers */

      function showPopup(map, content, coords) {
        if (popup.current) {
          map.removeChild(popup.current)
        }

        popup.current = new ymaps3.YMapMarker({ coordinates: coords, zIndex: 2000, source: 'equipment' }, content)
        map.addChild(popup.current)
      }

      const generateContentPin = (coords: [number, number], e: IEquipment): HTMLElement => {
        return mapEquipmentRenderer.getPinContent(e, {
          onClick: () => {
            const content = mapEquipmentRenderer.getMarkerPopupContent(e, {
              onClose: () => map.removeChild(popup.current),
              onNavigate: () => navigate(urls.liftScreen(e.id)),
            })
            showPopup(map, content, coords)
          },
        })
      }

      const marker = (feature) =>
        new ymaps3.YMapMarker(
          {
            coordinates: feature.geometry.coordinates,
            source: 'equipment',
            zIndex: 1800,
            properties: feature.properties,
          },
          generateContentPin(feature.geometry.coordinates, feature.properties)
        )

      const cluster = (coordinates, features) => {
        return new ymaps3.YMapMarker(
          {
            coordinates,
            source: 'equipment',
          },
          mapEquipmentRenderer
            .getCountCircleContent(features.map((item) => item.properties))
            .cloneNode(true) as HTMLElement
        )
      }

      const points = props.equipment
        .filter((e) => Boolean(e.location))
        .map((e) => ({
          type: 'Feature',
          id: e.id,
          geometry: { coordinates: [+e.location.longitude, +e.location.latitude] },
          properties: { ...e },
        }))

      const clusterer = new YMapClusterer({
        method: clusterByGrid({ gridSize: 64 }),
        features: points as any,
        marker,
        cluster,
      })

      clustererRef.current = clusterer

      const listener = new ymaps3.YMapListener({ layer: 'any', onActionEnd: handleActionEnd } as any)
      map.addChild(listener)
      map.addChild(clusterer)

      mapRef.current = map
    }

    renderMap()

    return () => {
      mapRef.current?.destroy()
    }
  }, [])

  useSkipFirstRenderEffect(() => {
    if (clustererRef.current) {
      const points = props.equipment
        .filter((e) => Boolean(e.location))
        .map((e) => ({
          type: 'Feature',
          id: e.id,
          geometry: { coordinates: [+e.location.longitude, +e.location.latitude] },
          properties: { ...e },
        }))

      clustererRef.current.update({
        features: points as any,
      })
    }
  }, [props.equipment])

  return (
    <div className={css.wrapper} style={props.wrapperStyle}>
      <div ref={ref} style={{ position: 'absolute', width: '100%', height: '100%', cursor: 'grab' }} />
    </div>
  )
}

export default Map
